import { AppliedTaxRate, isAppliedTaxRate, Note } from '../invoice';
import { Currency, currencyFromApi, currencyToApi } from '../pricing';
import { isTaxRate, TaxRate } from '../tax-rate';
import {
  CreditNoteInterface as ApiCreditNote,
  CreditNoteReason as ApiCreditNoteReason,
  AppliedReferenceType as ApiCreditNoteAppliedReferenceType,
  AppliedToInterface as ApiCreditNoteAppliedTo,
  LineItemInterface as ApiCreditNoteLineItem,
  CreditNoteInterface,
  CreditNoteType as ApiCreditNoteType,
} from '@vendasta/billing';
import { convertAPINote, isNote } from '../note/note';
import { convertApiAppliedTaxRate, convertApiTaxRate, convertAppliedTaxRate, convertTaxRate } from '../invoice/invoice';

export interface CreditNote {
  id?: string;
  merchantId: string;
  number?: string;
  customerId: string;
  subtotal?: number;
  total?: number;
  created?: Date;
  updated?: Date;
  items: CreditNoteLineItem[];
  currency: Currency;
  amountTax?: number;
  appliedTaxes?: AppliedTaxRate[];
  customerName: string;
  contactId: string;
  memo: string;
  notes: Note[];
  additionalContactIds: string[];
  reason: CreditNoteReason;
  appliedTo: CreditNoteAppliedTo[];
}

export interface CreditNoteLineItem {
  id: string;
  sku: string;
  amount: number;
  quantity: number;
  description: string;
  total: number;
  appliedTaxes: AppliedTaxRate[];
  taxRates: TaxRate[];
  subtotal: number;
}

export interface CreditNoteAppliedTo {
  referenceId: string;
  referenceType: CreditNoteAppliedReferenceType;
  referenceNumber?: string;
  amount: number;
  shouldRefund?: boolean;
}

export enum CreditNoteReason {
  UNSPECIFIED = 'Unspecified',
  DUPLICATE_CHARGE = 'Duplicate Charge',
  PRODUCT_UNSATISFACTORY = 'Product Unsatisfactory',
  ORDER_CHANGE = 'Order Change',
  OTHER = 'Other',
}

export enum CreditNoteAppliedReferenceType {
  UNSPECIFIED = 'Unspecified',
  INVOICE = 'Invoice',
}

export enum CreditNoteType {
  UNSPECIFIED = 'Unspecified',
  PRE_PAYMENT = 'pre_payment',
  POST_PAYMENT = 'post_payment',
}

export function creditNoteFromApi(apiCreditNote: ApiCreditNote): CreditNote {
  const zeroDate = new Date('0001-01-01T00:00:00Z').valueOf();

  const lineItems: CreditNoteLineItem[] = (apiCreditNote.items || [])
    .map((item) => convertApiCreditNoteLineItem(item))
    .filter(isCreditNoteLineItem);
  const appliedTaxes: AppliedTaxRate[] = (apiCreditNote.appliedTaxes || [])
    .map((at) => convertApiAppliedTaxRate(at))
    .filter(isAppliedTaxRate);
  const notes: Note[] = (apiCreditNote.notes || []).map((n) => convertAPINote(n)).filter(isNote);

  return {
    id: apiCreditNote.id,
    merchantId: apiCreditNote.merchantId ?? '',
    number: apiCreditNote.number,
    customerId: apiCreditNote.customerId ?? '',
    subtotal: apiCreditNote.subtotal ?? 0,
    total: apiCreditNote.total ?? 0,
    items: lineItems,
    currency: currencyFromApi(apiCreditNote.currency),
    amountTax: apiCreditNote.amountTax ?? 0,
    appliedTaxes: appliedTaxes,
    customerName: apiCreditNote.customerName ?? '',
    contactId: apiCreditNote.contactId ?? '',
    memo: apiCreditNote.memo ?? '',
    notes: notes,
    additionalContactIds: apiCreditNote.additionalContactIds ?? [],
    reason: convertApiCreditNoteReason(apiCreditNote.reason),
    appliedTo: convertApiCreditNoteAppliedTo(apiCreditNote.appliedTo ?? []),
    created:
      apiCreditNote.created && apiCreditNote.created?.valueOf() !== zeroDate
        ? new Date(apiCreditNote.created)
        : undefined,
    updated:
      apiCreditNote.updated && apiCreditNote.updated?.valueOf() !== zeroDate
        ? new Date(apiCreditNote.updated)
        : undefined,
  };
}

export function creditNoteToApi(creditNote: CreditNote): CreditNoteInterface {
  return {
    merchantId: creditNote.merchantId,
    customerId: creditNote.customerId,
    items: lineItemsToApi(creditNote.items),
    currency: currencyToApi(creditNote.currency),
    customerName: creditNote.customerName,
    contactId: creditNote.contactId,
    memo: creditNote.memo,
    notes: creditNote.notes,
    additionalContactIds: creditNote.additionalContactIds,
    reason: creditNoteReasonToApi(creditNote.reason),
    appliedTo: appliedToToApi(creditNote.appliedTo),
  };
}

function lineItemsToApi(lineItems: CreditNoteLineItem[]): ApiCreditNoteLineItem[] {
  return lineItems.map((lineItem) => ({
    id: lineItem.id,
    sku: lineItem.sku,
    amount: lineItem.amount,
    quantity: lineItem.quantity,
    description: lineItem.description,
    total: lineItem.total,
    appliedTaxes: (lineItem.appliedTaxes || []).map(convertAppliedTaxRate)?.filter?.(Boolean),
    taxRates: lineItem.taxRates?.map(convertTaxRate),
    subtotal: lineItem.subtotal,
  }));
}

function creditNoteReasonToApi(reason: CreditNoteReason): ApiCreditNoteReason {
  switch (reason) {
    case CreditNoteReason.DUPLICATE_CHARGE:
      return ApiCreditNoteReason.CREDIT_NOTE_REASON_DUPLICATE_CHARGE;
    case CreditNoteReason.PRODUCT_UNSATISFACTORY:
      return ApiCreditNoteReason.CREDIT_NOTE_REASON_PRODUCT_UNSATISFACTORY;
    case CreditNoteReason.ORDER_CHANGE:
      return ApiCreditNoteReason.CREDIT_NOTE_REASON_ORDER_CHANGE;
    case CreditNoteReason.OTHER:
      return ApiCreditNoteReason.CREDIT_NOTE_REASON_OTHER;
    default:
      return ApiCreditNoteReason.CREDIT_NOTE_REASON_UNSPECIFIED;
  }
}

function appliedToToApi(appliedTo: CreditNoteAppliedTo[]): ApiCreditNoteAppliedTo[] {
  return appliedTo?.map((a) => ({
    amount: a.amount,
    referenceId: a.referenceId,
    referenceType: appliedReferenceTypeToApi(a.referenceType),
    shouldRefund: a.shouldRefund,
  }));
}

function appliedReferenceTypeToApi(referenceType: CreditNoteAppliedReferenceType): ApiCreditNoteAppliedReferenceType {
  switch (referenceType) {
    case CreditNoteAppliedReferenceType.INVOICE:
      return ApiCreditNoteAppliedReferenceType.APPLIED_REFERENCE_TYPE_INVOICE;
    default:
      return ApiCreditNoteAppliedReferenceType.APPLIED_REFERENCE_TYPE_UNSPECIFIED;
  }
}

export function convertApiCreditNoteType(creditNoteType: ApiCreditNoteType | undefined): CreditNoteType {
  switch (creditNoteType) {
    case ApiCreditNoteType.CREDIT_NOTE_PRE_PAYMENT:
      return CreditNoteType.PRE_PAYMENT;
    case ApiCreditNoteType.CREDIT_NOTE_POST_PAYMENT:
      return CreditNoteType.POST_PAYMENT;
    default:
      return CreditNoteType.UNSPECIFIED;
  }
}

const isCreditNoteLineItem = (lineItem: CreditNoteLineItem | undefined): lineItem is CreditNoteLineItem => {
  return !!lineItem;
};

export function convertApiCreditNoteLineItem(lineItem: ApiCreditNoteLineItem): CreditNoteLineItem | undefined {
  if (!lineItem) {
    return undefined;
  }

  const appliedTaxes: AppliedTaxRate[] = (lineItem.appliedTaxes || [])
    .map((at) => convertApiAppliedTaxRate(at))
    .filter(isAppliedTaxRate);
  const taxRates: TaxRate[] = (lineItem.taxRates || []).map((tr) => convertApiTaxRate(tr)).filter(isTaxRate);

  return {
    id: lineItem.id || '',
    sku: lineItem.sku || '',
    amount: lineItem.amount || 0,
    quantity: lineItem.quantity || 0,
    description: lineItem.description || '',
    subtotal: lineItem.subtotal || 0,
    total: lineItem.total || 0,
    appliedTaxes: appliedTaxes || [],
    taxRates: taxRates || [],
  };
}

function convertApiCreditNoteReason(r?: ApiCreditNoteReason): CreditNoteReason {
  switch (r) {
    case ApiCreditNoteReason.CREDIT_NOTE_REASON_DUPLICATE_CHARGE:
      return CreditNoteReason.DUPLICATE_CHARGE;
    case ApiCreditNoteReason.CREDIT_NOTE_REASON_PRODUCT_UNSATISFACTORY:
      return CreditNoteReason.PRODUCT_UNSATISFACTORY;
    case ApiCreditNoteReason.CREDIT_NOTE_REASON_ORDER_CHANGE:
      return CreditNoteReason.ORDER_CHANGE;
    case ApiCreditNoteReason.CREDIT_NOTE_REASON_OTHER:
      return CreditNoteReason.OTHER;
    default:
      return CreditNoteReason.UNSPECIFIED;
  }
}

function convertApiCreditNoteAppliedTo(r: ApiCreditNoteAppliedTo[]): CreditNoteAppliedTo[] {
  const applied: CreditNoteAppliedTo[] = [];
  r?.forEach((a) => {
    const appliedTo = {
      amount: a.amount || 0,
      referenceId: a.referenceId || '',
      referenceNumber: a.referenceNumber,
      referenceType: convertApiCreditNoteAppliedReferenceType(a.referenceType),
    };
    applied.push(appliedTo);
  });

  return applied;
}

function convertApiCreditNoteAppliedReferenceType(
  r: ApiCreditNoteAppliedReferenceType | undefined,
): CreditNoteAppliedReferenceType {
  switch (r) {
    case ApiCreditNoteAppliedReferenceType.APPLIED_REFERENCE_TYPE_INVOICE:
      return CreditNoteAppliedReferenceType.INVOICE;
    default:
      return CreditNoteAppliedReferenceType.UNSPECIFIED;
  }
}
